/**
 * HoursOfRestSidebar - Sidebar content for hours of rest with quick actions and compliance info
 */

import React, { useState, useMemo } from 'react';
import { Clock, FileText, Calendar, BookOpen, Download, AlertTriangle, CheckCircle, Zap } from 'lucide-react';
import { toast } from 'react-hot-toast';
import HoursExportModal from './HoursExportModal';
import { useHoursOfRestData } from '../../../../hooks/domain/useHoursOfRestData';
import { useVessel } from '../../../../contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
import { usePermissions } from '../../../../hooks/usePermissions';

const HoursOfRestSidebar = ({ compliance, weeklyStats, crewWithIssues, onRecordHours, currentVessel }) => {
  const [showGuide, setShowGuide] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const { currentVesselId } = useVessel();
  // Use new unified crew system
  const { crew } = useCrew();
  const { can } = usePermissions();

  // FT-164: Use new flat array hook
  const { data: horRecords = [] } = useHoursOfRestData();

  // FT-164: Convert flat array to legacy nested lookup for compatibility
  const hoursData = useMemo(() => {
    const nested = {};
    for (const record of horRecords) {
      if (!record.crewId || !record.date) continue;
      if (!nested[record.crewId]) nested[record.crewId] = {};
      nested[record.crewId][record.date] = record;
    }
    return nested;
  }, [horRecords]);
  
  return (
    <div className="space-y-6">
      {/* FT-30: New 4-indicator Compliance Summary */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Clock className="h-5 w-5 text-white" />
            Hours of Rest Compliance
          </h3>
        </div>
        <div className="p-6">
          {/* FT-165: Simplified to 2 MCA compliance indicators (MGN 505) */}
          {/* Removed Recording and Signed - they create false red flags */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {/* 7-day rest indicator */}
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                compliance.sevenDayRest?.violation > 0 ? 'bg-red-100 text-red-800' :
                compliance.sevenDayRest?.warning > 0 ? 'bg-amber-100 text-amber-800' :
                'bg-green-100 text-green-800'
              }`}>
                {compliance.sevenDayRest?.violation > 0 ? '🔴' :
                 compliance.sevenDayRest?.warning > 0 ? '🟡' : '🟢'} 7-day
              </span>

              {/* 24h rest indicator */}
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                compliance.dailyRest?.violation > 0 ? 'bg-red-100 text-red-800' :
                'bg-green-100 text-green-800'
              }`}>
                {compliance.dailyRest?.violation > 0 ? '🔴' : '🟢'} 24h
              </span>
            </div>
          </div>

          {/* Compliance stats */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                <span className="text-sm text-gray-600">Compliant crew</span>
              </div>
              <span className="text-sm font-medium text-gray-900">{compliance.compliant}</span>
            </div>

            {compliance.nonCompliant > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
                  <span className="text-sm text-gray-600">Non-compliant</span>
                </div>
                <span className="text-sm font-medium text-red-600">{compliance.nonCompliant}</span>
              </div>
            )}

            <div className="pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Average weekly hours: <span className="font-medium text-gray-900">{weeklyStats.avgHoursWorked}h</span>
              </p>
            </div>
          </div>

          {/* Non-compliant crew list */}
          {crewWithIssues.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-900 mb-2">Crew needing attention:</p>
              <div className="space-y-1">
                {crewWithIssues.slice(0, 3).map(crew => (
                  <p key={crew.id} className="text-sm text-red-600">
                    • {crew.full_name || crew.name}
                  </p>
                ))}
                {crewWithIssues.length > 3 && (
                  <p className="text-sm text-gray-500">
                    and {crewWithIssues.length - 3} more...
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Zap className="h-5 w-5 text-white" />
            Quick Actions
          </h3>
        </div>
        <div className="p-6 space-y-3">
          {/* Only show Record Hours button for Fleet/Skipper */}
          {can('update', 'hours_of_rest') && (
            <button 
              onClick={onRecordHours}
              className="w-full flex items-center justify-start px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Record Hours
            </button>
          )}
          
          <button 
            onClick={() => setShowExportModal(true)}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Monthly Report
          </button>
          
          <button
            onClick={() => toast('Bulk hours entry feature coming soon!', { duration: 3000, icon: '📋' })}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FileText className="h-4 w-4 mr-2" />
            Bulk Hours Entry
          </button>
          
          <button 
            onClick={() => setShowGuide(true)}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Compliance Guide
          </button>
        </div>
      </div>

      {/* Hours of Rest Requirements */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-white" />
            Hours of Rest Requirements
          </h3>
          <p className="mt-0.5 text-sm text-slate-300">WBC3 seagoing vessel requirements</p>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {/* Seagoing Vessels - WBC3 */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Rest Requirements (MGN 505)</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <p>• Minimum 10 hours rest in any 24-hour period</p>
                <p>• Minimum 77 hours rest in any 7-day period</p>
                <p>• Rest may be divided into max 2 periods</p>
                <p>• One period must be at least 6 hours</p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-600">
                <FileText className="h-4 w-4 mr-2" />
                <span>MGN 505 (M+F) - Hours of Work and Manning</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowGuide(false)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <BookOpen className="h-6 w-6 text-cyan-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Hours of Rest Compliance Guide</h2>
                </div>
                <button
                  onClick={() => setShowGuide(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Recording Requirements</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-1.5"></span>
                      Record hours daily for all crew members
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-1.5"></span>
                      Maintain records for at least 12 months
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-3 mt-1.5"></span>
                      Records must be endorsed monthly by master and crew
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Compliance Checking</h3>
                  <p className="text-gray-600 mb-3">
                    Compliance is checked at the end of each month. Empty days are counted as 24 hours rest
                    to allow for shore leave and days off.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-gray-900">WBC3 Seagoing Vessels:</p>
                      <p className="text-gray-600 text-sm">
                        Violations flagged when daily rest falls below 10 hours or weekly rest below 77 hours.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-1">Regulatory Reference</h4>
                  <p className="text-sm text-blue-800">
                    Maritime and Coastguard Agency MGN 505 (M+F) - Hours of Work and Manning
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                <button
                  onClick={() => setShowGuide(false)}
                  className="w-full sm:w-auto px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-md hover:bg-cyan-700"
                >
                  Close Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <HoursExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        crew={crew}
        hoursData={hoursData}
      />
    </div>
  );
};

export default HoursOfRestSidebar;